import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircleOutlined,
  CloudDownloadOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  MailOutlined,
  PlusOutlined,
  SendOutlined,
  SettingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  ContentBackground,
  ContentBody,
  ContentHeader,
  ContentWrapper,
} from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { HowToCard } from '@/components/how-to-card';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES_PERMISSIONS } from '@/constants';
import { API } from '@/services/api.service';
import AddCompanyModal from './_components/AddCompanyModal';
import CompanyDrawer from './_components/CompanyDrawer';
import SettingsModal from './_components/SettingsModal';
import {
  FINAL_QUARTER,
  QUARTERS,
  STATUS_COLORS,
  currentPeriod,
  date,
  deadlineOf,
  errorText,
  money,
  saveBlob,
} from './_components/format';

// Status filters in display order; MANUAL = made by hand outside the platform.
const STATUSES = ['READY', 'INCOMPLETE', 'MANUAL', 'SENT'];

/**
 * NBS form DI1 for every client with foreign owners, one quarter at a time:
 * which forms are ready, what is missing, and the files to mail to NBS.
 *
 * The period and the open company live in the address, so a link or a reload
 * lands on the same place.
 *
 * @returns {JSX.Element}
 */
function DiReportsContent() {
  const { t } = useTranslation(['diReports', 'menus']);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const fallback = currentPeriod();
  const year = Number(searchParams.get('year')) || fallback.year;
  const quarter = Number(searchParams.get('quarter')) || fallback.quarter;
  const openCompany = searchParams.get('company');
  const period = { year, quarter };

  const setParam = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, String(v)) : next.delete(k)
    );
    setSearchParams(next, { replace: true });
  };

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['di-reports', year, quarter],
    queryFn: () => API.diReports.getQuarter(period),
  });
  const { data: settings } = useQuery({
    queryKey: ['di-reports-settings'],
    queryFn: () => API.diReports.getSettings(),
  });

  const items = useMemo(() => data?.items || [], [data]);
  const counts = useMemo(
    () => ({
      READY: items.filter((i) => i.status === 'READY').length,
      INCOMPLETE: items.filter((i) => i.status === 'INCOMPLETE').length,
      MANUAL: items.filter((i) => i.status === 'MANUAL').length,
      SENT: items.filter((i) => i.status === 'SENT').length,
    }),
    [items]
  );
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(
      (i) =>
        (!statusFilter || i.status === statusFilter) &&
        (!q || i.name.toLowerCase().includes(q) || i.mb.includes(q))
    );
  }, [items, search, statusFilter]);

  const sentMutation = useMutation({
    mutationFn: ({ companyId, sent }) =>
      API.diReports.setSent(companyId, period, sent),
    onSuccess: () => {
      message.success(t('messages.saved'));
      queryClient.invalidateQueries({
        queryKey: ['di-reports', year, quarter],
      });
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const downloadOne = async (row) => {
    setDownloading(row.companyId);
    try {
      const { blob, fileName } = await API.diReports.downloadFile(
        row.companyId,
        period
      );
      saveBlob(blob, fileName);
    } catch (e) {
      message.error(await errorText(e));
    } finally {
      setDownloading(null);
    }
  };

  const downloadAll = async () => {
    setDownloading('all');
    try {
      const { blob, fileName, count, skipped } =
        await API.diReports.downloadZip(period);
      saveBlob(blob, fileName);
      message.success(t('messages.zipDone', { count, skipped }));
    } catch (e) {
      message.error(await errorText(e));
    } finally {
      setDownloading(null);
    }
  };

  const deadline = deadlineOf(year, quarter);
  const daysLeft = deadline.startOf('day').diff(new Date(), 'day');
  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now - 2, now - 1, now].map((y) => ({ value: y, label: String(y) }));
  }, []);

  const guide = [
    {
      title: t('howTo.quarter.title'),
      steps: [
        {
          icon: ExclamationCircleOutlined,
          title: t('howTo.quarter.s1.title'),
          text: t('howTo.quarter.s1.text'),
          onClick: () => setStatusFilter('INCOMPLETE'),
        },
        {
          icon: EditOutlined,
          title: t('howTo.quarter.s2.title'),
          text: t('howTo.quarter.s2.text'),
        },
        {
          icon: CloudDownloadOutlined,
          title: t('howTo.quarter.s3.title'),
          text: t('howTo.quarter.s3.text'),
          onClick: downloadAll,
        },
        {
          icon: MailOutlined,
          title: t('howTo.quarter.s4.title'),
          text: t('howTo.quarter.s4.text'),
        },
        {
          icon: SendOutlined,
          title: t('howTo.quarter.s5.title'),
          text: t('howTo.quarter.s5.text'),
        },
      ],
    },
  ];

  const columns = [
    {
      title: t('columns.company'),
      dataIndex: 'name',
      width: 300,
      sorter: (a, b) => a.name.localeCompare(b.name, 'sr'),
      render: (name, row) => (
        <div className="min-w-0">
          <a
            className="block truncate"
            title={name}
            onClick={() => setParam({ company: row.companyId })}
          >
            {name}
          </a>
          <div className="text-xs opacity-60">
            {t('columns.mb')} {row.mb}
            {row.entityType === 26 && ` · ${t('entityType.26')}`}
          </div>
        </div>
      ),
    },
    {
      title: t('columns.status'),
      dataIndex: 'status',
      width: 140,
      filters: STATUSES.map((s) => ({
        text: t(`status.${s}`),
        value: s,
      })),
      onFilter: (value, row) => row.status === value,
      render: (status, row) => (
        <Tooltip title={status === 'SENT' ? date(row.sentAt) : null}>
          <Tag color={STATUS_COLORS[status]} className="m-0">
            {t(`status.${status}`)}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: t('columns.problems'),
      key: 'problems',
      ellipsis: { showTitle: false },
      render: (_, row) =>
        row.manual ? (
          <Tooltip title={row.firstIssue} placement="topLeft">
            <span className="text-sm opacity-70">
              {t('status.MANUAL')} · {row.firstIssue}
            </span>
          </Tooltip>
        ) : row.errors || row.warnings ? (
          <Tooltip title={row.firstIssue} placement="topLeft">
            <span className="text-sm">
              <span className={row.errors ? 'text-red-600' : 'text-amber-600'}>
                {row.errors
                  ? t('columns.errors', { count: row.errors })
                  : t('columns.warnings', { count: row.warnings })}
              </span>
              <span className="opacity-70"> · {row.firstIssue}</span>
            </span>
          </Tooltip>
        ) : (
          <CheckCircleOutlined className="text-green-600" />
        ),
    },
    {
      title: t('columns.capital'),
      key: 'capital',
      align: 'right',
      width: 130,
      render: (_, row) => money(row.table4Usd?.f30),
    },
    {
      title: t('columns.result'),
      key: 'result',
      align: 'right',
      width: 130,
      render: (_, row) => money(row.table4Usd?.f341),
    },
    {
      title: t('columns.ab'),
      key: 'ab',
      width: 120,
      render: (_, row) =>
        row.ledger ? (
          <Tooltip
            title={t('columns.abTooltip', {
              date: date(row.ledger.extractedAt),
            })}
          >
            <span>{date(row.ledger.lastEntryDate)}</span>
          </Tooltip>
        ) : (
          <span className="text-xs opacity-60">
            {row.abKey ? t('columns.abWaiting') : t('columns.abNone')}
          </span>
        ),
    },
    {
      title: t('columns.apr'),
      key: 'apr',
      width: 110,
      render: (_, row) => (
        <Tooltip title={row.aprStatus || null}>
          <span className="text-xs">{date(row.aprFetchedAt)}</span>
        </Tooltip>
      ),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title={t('actions.open')}>
            <Button
              shape="circle"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setParam({ company: row.companyId })}
            />
          </Tooltip>
          {!row.manual && (
            <Tooltip
              title={
                row.status === 'INCOMPLETE'
                  ? t('actions.downloadIncomplete')
                  : t('actions.download')
              }
            >
              <Button
                shape="circle"
                size="small"
                icon={<FileExcelOutlined />}
                loading={downloading === row.companyId}
                onClick={() => downloadOne(row)}
              />
            </Tooltip>
          )}
          {(row.status === 'READY' || row.status === 'MANUAL') && (
            <Tooltip title={t('actions.markSent')}>
              <Button
                shape="circle"
                size="small"
                icon={<SendOutlined />}
                loading={
                  sentMutation.isPending &&
                  sentMutation.variables?.companyId === row.companyId
                }
                onClick={() =>
                  sentMutation.mutate({ companyId: row.companyId, sent: true })
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <RootLayout metadata={{ title: t('diReports', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('diReports', { ns: 'menus' })}>
              <Button
                shape="round"
                icon={<DownloadOutlined />}
                loading={downloading === 'all'}
                disabled={!counts.READY}
                onClick={downloadAll}
              >
                {t('actions.downloadAll', { count: counts.READY })}
              </Button>
              <Button
                shape="round"
                icon={<PlusOutlined />}
                onClick={() => setAddOpen(true)}
              >
                {t('actions.addCompany')}
              </Button>
              <Button
                shape="round"
                icon={<SettingOutlined />}
                onClick={() => setSettingsOpen(true)}
              >
                {t('actions.settings')}
              </Button>
            </ContentHeader>
            <ContentBody>
              <div className="with-y-overflow">
                <div className="grid gap-4 p-4">
                  <HowToCard
                    title={t('howTo.title')}
                    sections={guide}
                    note={t('howTo.note')}
                    open={data ? !items.length : undefined}
                  />

                  <div className="flex flex-wrap items-center gap-3">
                    <Select
                      value={year}
                      options={years}
                      onChange={(v) => setParam({ year: v, company: null })}
                      style={{ width: 100 }}
                    />
                    <Segmented
                      value={quarter}
                      onChange={(v) => setParam({ quarter: v, company: null })}
                      options={QUARTERS.map((q) => ({
                        value: q,
                        label:
                          q === FINAL_QUARTER
                            ? t('quarter.final')
                            : t('quarter.q', { q }),
                      }))}
                    />
                    <span className="text-sm">
                      {t('period.deadline')}:{' '}
                      <strong>{deadline.format('DD.MM.YYYY')}</strong>
                      {daysLeft >= 0 && daysLeft <= 30 && (
                        <Tag
                          className="ml-2"
                          color={daysLeft <= 5 ? 'red' : 'gold'}
                        >
                          {t('period.daysLeft', { count: daysLeft })}
                        </Tag>
                      )}
                    </span>
                    <span className="text-sm opacity-70">
                      {t('period.rate', { date: date(data?.periodEnd) })}:{' '}
                      <strong>{data?.rate ? money(data.rate, 4) : '—'}</strong>
                    </span>
                  </div>

                  {settings &&
                    (!settings.contact?.firstName ||
                      !settings.contact?.email) && (
                      <Alert
                        type="warning"
                        showIcon
                        className="!static"
                        message={t('settings.missingContact')}
                        action={
                          <Button
                            shape="round"
                            size="small"
                            onClick={() => setSettingsOpen(true)}
                          >
                            {t('actions.settings')}
                          </Button>
                        }
                      />
                    )}

                  <div className="flex flex-wrap items-center gap-2">
                    {STATUSES.map((s) => (
                      <Tag.CheckableTag
                        key={s}
                        checked={statusFilter === s}
                        onChange={(checked) =>
                          setStatusFilter(checked ? s : null)
                        }
                      >
                        {s === 'INCOMPLETE' ? (
                          <WarningOutlined />
                        ) : s === 'MANUAL' ? (
                          <EditOutlined />
                        ) : (
                          <CheckCircleOutlined />
                        )}{' '}
                        {t(`status.${s}`)}: {counts[s]}
                      </Tag.CheckableTag>
                    ))}
                    <Input.Search
                      allowClear
                      placeholder={t('search')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ maxWidth: 280 }}
                      className="ml-auto"
                    />
                  </div>

                  <Table
                    rowKey="companyId"
                    size="small"
                    loading={isLoading}
                    columns={columns}
                    dataSource={rows}
                    pagination={{ pageSize: 50, showSizeChanger: false }}
                    scroll={{ x: 1150 }}
                    tableLayout="fixed"
                  />
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>

      <CompanyDrawer
        companyId={openCompany}
        period={period}
        onClose={() => setParam({ company: null })}
        onChanged={() => refetch()}
      />
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={() => refetch()}
      />
      <AddCompanyModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={(companyId) => {
          setAddOpen(false);
          refetch();
          setParam({ company: companyId });
        }}
      />
    </RootLayout>
  );
}

const DiReportsPage = withPrivateRoute(
  DiReportsContent,
  ROLES_PERMISSIONS.CLIENT_REPORTS.VIEW
);
DiReportsPage.displayName = 'DiReportsPage';

export default DiReportsPage;
